import React from 'react';
import { getDealRoomBrandedName, getDealRoomSingleWord } from '../utils/locale';
import { IntlProvider } from 'react-intl';
import getMessages from '../lib/i18n/getMessages';
import { CurrencyProvider } from '../hooks/useCurrency';
// import TopCategories from '../components/Home/TopCategories';
import GettingStartedCarousel from '../components/GettingStarted/GettingStartedCarousel';
import ExploreTabs from '../components/Succession/Home/ExploreTabs';
import ExploreCarousel from '../components/Succession/Home/ExploreCarousel';
import MarketplaceFeedContainer from './MarketplaceFeedContainer';
import PartialSalesMiniList from '../components/Succession/PartialSales/PartialSalesMiniList';
import PartnerMiniGrid from '../components/Succession/PartnerMiniGrid';
import RolodexMiniCard from '../components/Succession/RolodexMiniCard';
import SellerHeatMap from '../components/Succession/SellerHeatMap';
import ValueGapDcfSnapshot from '../components/Succession/ValueGapDcfSnapshot';
import MiniSprintDashboard from '../components/Succession/MiniSprintDashboard';
import WealthGapMiniCard from '../components/Succession/WealthGapMiniCard';
import AIAssistantWidget from '../components/Succession/AgentSystem/AIAssistantWidget';
import { SnackbarProvider } from 'notistack';
import SuccessionThemeProvider from '../components/Succession/SuccessionThemeProvider';
import SuccessionDrawerWithListener from '../containers/Succession/SuccessionDrawerWithListener';
import ContinuityPlanCard from '../components/Succession/ContinuityPlanCard';
import CatastrophicPlanCard from '../components/Succession/CatastrophicPlanCard';
import MyPartialSaleMetricsCard from '../components/Succession/MyPartialSaleMetricsCard';
import SuccessionEducationCard from '../components/Succession/SuccessionEducationCard';
import HomeVmiPanel from '../components/Succession/Home/InsightsDropdown/HomeVmiPanel';
import YourActivity from '../components/Succession/YourActivity';
import ValuationTrend from '../components/Succession/Home/InsightsDropdown/ValuationTrend';
import ValuationInsight from '../components/Succession/Home/InsightsDropdown/ValuationInsight';
import InsightsSprintProgress from '../components/Succession/Home/InsightsDropdown/InsightsSprintProgress';
import GrowthExitDecisionCard from '../components/Succession/GrowthExitDecisionCard';
import DealRoomInsights from '../components/Succession/Home/InsightsDropdown/DealRoomInsights';
import GettingStartedCard from '../components/GettingStarted/GettingStartedCard';

class HomeContainer extends React.Component {
  constructor(props) {
    super(props);
    
    // Set default active tab based on user's motivation
    const defaultTab = this.getDefaultTabFromMotivation(props.current_user);
    
    this.state = { activeTab: defaultTab, pageByTab: { opportunity: 0, growth: 0, succession: 0, learn: 0 }, insightsView: 'valuation_insight', windowWidth: (typeof window !== 'undefined' ? window.innerWidth : 1440), _hideDecisionCard: false, firstIndex: 0, dealRoomWord: 'Deal Room', _rightPanel: 'getting_started' };
  }

  getDefaultTabFromMotivation = (user) => {
    const raw = (user && (user.primary_motivation || user.motivation)) || null;
    if (!raw) {
      return 'growth'; // Default to growth if no motivation
    }

    const motivation = String(raw).toLowerCase();
    
    // motivation == succession default to Succession
    if (motivation === 'succession') {
      return 'succession';
    }
    
    // motivation == growth or motivation == no_motivation default to Growth
    if (motivation === 'growth' || motivation === 'no_motivation') {
      return 'growth';
    }
    
    // Default fallback
    return 'growth';
  }

  componentDidMount() {
    try {
      this._onResize = () => {
        try { this.setState({ windowWidth: window.innerWidth }); } catch (_) {}
      };
      window.addEventListener('resize', this._onResize);
    } catch (_) {}

    // Observe theme changes to ensure insights card background updates without hard refresh
    try {
      const computeIsDark = () => {
        try { return document.documentElement.classList.contains('succession-dark') || document.body.classList.contains('succession-dark'); } catch (_) { return false; }
      };
      // Initial capture
      this.setState({ _isDarkTheme: computeIsDark() });
      this._themeObserver = new MutationObserver(() => {
        const next = computeIsDark();
        // Only update when value actually changes to avoid re-render loops
        if (this.state._isDarkTheme !== next) {
          try { this.setState({ _isDarkTheme: next }); } catch (_) {}
        }
      });
      this._themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    } catch (_) {}

    // Ensure default tab reflects latest user motivation from store after mount
    try {
      const params = new URLSearchParams(window.location.search || '');
      const forced = params.get('tab');
      if (!forced) {
        const state = (typeof document !== 'undefined' && document.store?.getState?.()) || {};
        const mergedUser = { ...(this.props.current_user || {}), ...(state.user || {}) };
        const desired = this.getDefaultTabFromMotivation(mergedUser);
        if (desired && desired !== this.state.activeTab) {
          this.setState({ activeTab: desired });
        }
      }
    } catch (_) {}

    // Handle post-wizard redirect parameters to open Growth tab and start guided tour
    try {
      const params = new URLSearchParams(window.location.search || '');
      const fromWizard = params.get('postWizardTour') === '1';
      const forceTab = params.get('tab');
      const insights = params.get('insights');
      if (fromWizard) {
        if (forceTab) this.setState({ activeTab: forceTab });
        if (insights) this.setState({ insightsView: insights });
        // Defer opening the guided tour until after first paint
        setTimeout(() => {
          try {
            const steps = [
              { id: 'growth-tab', title: 'Growth Tab', description: 'Now that you’ve completed your readiness assessment, the Growth tab unlocks insights to help you measure and close your gaps. Let’s take a look.', targetSelector: '[data-tutorial-id="explore-tabs"] button#tab-growth, [data-tutorial-id="explore-tabs"]', position: 'bottom', onEnter: () => { try { document.querySelector('#tab-growth')?.click(); } catch(_) {} } },
              { id: 'wealth-gap', title: 'Wealth Gap', description: 'Here’s your Wealth Gap — this shows how much more you need to reach your retirement or exit goals. It’s your North Star number.', targetSelector: '[data-wealth-gap-card="true"]', position: 'right' },
              { id: 'value-gap', title: 'Value Gap', description: 'Your Value Gap shows the difference between what your business is worth today and what it could be worth if fully optimized.', targetSelector: '[data-tutorial-id="explore-cards"] .h-full:nth-child(2)', position: 'bottom' },
              { id: 'sprint-progress', title: 'Sprint Progress', description: 'Track your Sprint Progress here. These are 90-day action plans designed to help you improve your valuation and close your gaps step by step.', targetSelector: '[data-tutorial-id="sprint-card"]', position: 'left', onEnter: () => { try { const viewport = document.querySelector('[data-carousel-viewport="1"]'); const track = document.querySelector('[data-carousel-track="1"]'); if (!viewport || !track) return; const tabsOrder = ['opportunity', 'growth', 'succession', 'learn']; const groups = tabsOrder.map((id) => ({ id, items: this.getItemsForTab?.(id) || [] })); let base = 0; let growthBase = 0; for (const g of groups) { if (g.id === 'growth') { growthBase = base; break; } base += (g.items?.length || 0); } const sprintIdxInGroup = 2; const desiredIndex = growthBase + sprintIdxInGroup; const rect = viewport.getBoundingClientRect(); const sample = track.querySelector('div'); const w = sample ? Math.round(sample.getBoundingClientRect().width) : 0; const perPage = w ? Math.max(1, Math.floor(rect.width / w)) : 4; const aligned = Math.max(0, desiredIndex - (desiredIndex % perPage)); this.setState({ firstIndex: aligned }); } catch(_) {} } },
              { id: 'succession-readiness', title: 'Succession Readiness', description: 'You can always return to your Succession Readiness view. This radial graph shows your Value Maturity Index (VMI), helping you see where you’re strongest and where you need focus.', targetSelector: '[data-tutorial-id="succession-readiness-card"], [data-tutorial-id="insights-dropdown"] select', position: 'right', onEnter: () => { try { const sel = document.querySelector('[data-tutorial-id="insights-dropdown"] select'); if (sel) { sel.value = 'succession_readiness'; sel.dispatchEvent(new Event('change', { bubbles: true })); } } catch(_) {} } },
              { id: 'whats-next', title: "What's Next For You?", description: 'At any point, you can decide what’s next for your business. If you choose Grow, we’ll guide you with strategies to increase value and performance. If you choose Exit, we’ll create a tailored plan to help you prepare for succession and transition. The choice is always yours.', targetSelector: '[data-tutorial-id="whats-next"]', position: 'left' },
              { id: 'app-launcher', title: 'Explore All Tools', description: 'Open the app launcher to see all tools grouped by Succession, Opportunities, Growth, and Learn.', targetSelector: '[data-tutorial-id="app-launcher-button"]', position: 'left', onEnter: () => { try { window.__closeAppLauncher?.(); const btn = document.querySelector('[data-tutorial-id="app-launcher-button"]'); if (btn) btn.focus(); } catch(_) {} } },
            ];
            window.__guidedTourSteps = steps;
            // Ensure carousel is aligned to full-card boundaries before starting tour
            try {
              const tabsOrder = ['opportunity', 'growth', 'succession', 'learn'];
              const itemsByGroup = tabsOrder.map((id) => ({ id, items: this.getItemsForTab?.(id) || [] }));
              const groupStartIndex = {};
              let cursor = 0;
              itemsByGroup.forEach(({ id, items }) => { groupStartIndex[id] = cursor; cursor += (items?.length || 0); });
              const startForTab = groupStartIndex[forceTab || this.state.activeTab] || 0;
              if (this.state.firstIndex !== startForTab) this.setState({ firstIndex: startForTab });
            } catch (_) {}
            window.dispatchEvent(new CustomEvent('open-guided-tour'));
          } catch (_) {}
        }, 300);
      }
    } catch (_) {}

    // Fetch company deal-room word to ensure dropdown label is branded
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'same-origin',
        body: JSON.stringify({ query: `query DealRoomWord { getCurrentUser { company { deal_room_branded_name deal_room_branded_name_fr deal_room_single_word deal_room_single_word_fr } } }` }),
      }).then((r) => r.json()).then((json) => {
        try {
          const c = json?.data?.getCurrentUser?.company || {};
          const word = getDealRoomBrandedName(c) || getDealRoomSingleWord(c);
          if (word && word !== this.state.dealRoomWord) this.setState({ dealRoomWord: word });
        } catch (_) {}
      }).catch(() => {});
    } catch (_) {}

    // Initialize the starting index to the beginning of the default tab's group
    try {
      const tabsOrder = ['opportunity', 'growth', 'succession', 'learn'];
      const itemsByGroup = tabsOrder.map((id) => ({ id, items: this.getItemsForTab?.(id) || [] }));
      const groupStartIndex = {};
      let cursor = 0;
      itemsByGroup.forEach(({ id, items }) => {
        groupStartIndex[id] = cursor;
        cursor += (items?.length || 0);
      });
      const startForDefault = groupStartIndex[this.state.activeTab] || 0;
      this.setState({ firstIndex: startForDefault });
    } catch (_) {}
  }

  getItemsForTab = (tabId) => {
    if (tabId === 'opportunity') {
      const items = [
        (
          <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
            <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
              <PartnerMiniGrid />
            </div>
          </div>
        ),
        (
          <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
            <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
              <RolodexMiniCard />
            </div>
          </div>
        ),
        (<SellerHeatMap />),
        (
          <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
            <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
              <PartialSalesMiniList />
            </div>
          </div>
        ),
      ];
      // Feature gate: ai_assistant at first position when enabled
      try {
        const state = (typeof document !== 'undefined' && document.store?.getState?.()) || {};
        const userFromProps = this.props.current_user || {};
        const userFromStore = state.user || {};
        const companyFromProps = this.props.company || {};
        const companyFromStore = state.company || {};
        // Prefer store values, merge over props
        const user = { ...userFromProps, ...userFromStore };
        const company = { ...companyFromProps, ...companyFromStore };
        const userFeatureList = ([]) 
          .concat(Array.isArray(user.features) ? user.features : [])
          .concat(Array.isArray(user.feature_types) ? user.feature_types : []);
        const companyFeatureList = ([]) 
          .concat(Array.isArray(company.features) ? company.features : [])
          .concat(Array.isArray(company.feature_types) ? company.feature_types : []);
        const userHas = userFeatureList.some((f) => String(f) === 'ai_assistant');
        const companyHas = companyFeatureList.some((f) => String(f) === 'ai_assistant');
        try { if (typeof window !== 'undefined') {
          const gate = { userHas, companyHas, userFeatures: userFeatureList, companyFeatures: companyFeatureList };
          window.__aiAssistantGate = gate;
          // Use console.log so it shows at Default level
          // eslint-disable-next-line no-console
          console.log('[HomeContainer] ai_assistant gate', gate);
        }} catch(_) {}
        if (userHas || companyHas) {
          const name = (this.props.current_user && this.props.current_user.name) || (document.store?.getState?.().user?.name) || 'there';
          items.unshift(<AIAssistantWidget currentUser={this.props.current_user} integrated={true} />);
        }
      } catch (_) {}
      return items;
    }
    if (tabId === 'growth') {
      return [
        (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><WealthGapMiniCard /></div>),
        (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><ValueGapDcfSnapshot /></div>),
        (
          <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
            <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
              <MiniSprintDashboard />
            </div>
          </div>
        ),
      ];
    }
    if (tabId === 'succession') {
      return [
        (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><ContinuityPlanCard /></div>),
        (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><CatastrophicPlanCard /></div>),
        // Replace partial sale metrics with education resources
        (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><SuccessionEducationCard /></div>),
      ];
    }
    if (tabId === 'learn') {
      return [
        (
          <div className="bg-succession-bg-primary rounded-lg border border-succession-border p-6 text-succession-text-secondary">
            Community coming soon.
          </div>
        ),
      ];
    }
    return [];
  }

  componentWillUnmount() {
    try { if (this._onResize) window.removeEventListener('resize', this._onResize); } catch (_) {}
    try { if (this._themeObserver) { this._themeObserver.disconnect(); this._themeObserver = null; } } catch (_) {}
  }

  handleTabChange = (tabId) => {
    // Jump the sliding window to the first item of the selected tab's group
    try {
      const tabsOrder = ['opportunity', 'growth', 'succession', 'learn'];
      const itemsByGroup = tabsOrder.map((id) => ({ id, items: this.getItemsForTab?.(id) || [] }));
      const groupStartIndex = {};
      let cursor = 0;
      itemsByGroup.forEach(({ id, items }) => {
        groupStartIndex[id] = cursor;
        cursor += (items?.length || 0);
      });
      const nextFirst = groupStartIndex[tabId] ?? 0;
      this.setState({ activeTab: tabId, firstIndex: nextFirst });
    } catch (_) {
      this.setState({ activeTab: tabId });
    }
    try { if (window.ahoy) window.ahoy.track('home_explore_tab_view', { tab_name: tabId }); } catch (_) {}
  };

  render() {
    const { activeTab } = this.state;
    // Responsive visible count: 4 default, 3 when viewport <= 1500px (updated from 1300)
    const pageSize = (this.state.windowWidth && this.state.windowWidth <= 1500) ? 3 : 4;
    // Guard against fractional device pixels causing layout drift
    const normalizedWidth = Math.max(0, Math.floor(this.state.windowWidth || 0));
    if (normalizedWidth !== this.state.windowWidth) {
      // harmless state update to stabilize downstream calculations
      setTimeout(() => { try { this.setState({ windowWidth: normalizedWidth }); } catch (_) {} }, 0);
    }
    const getSlotsForWidth = (w) => {
      if (!w || typeof w !== 'number') return 4;
      if (w >= 1500) return 4; // xl and up (updated from 1280)
      if (w >= 1024) return 3; // lg
      if (w >= 768) return 2;  // md
      return 1;                // sm
    };
    const effectivePageSize = Math.min(pageSize, getSlotsForWidth(this.state.windowWidth));  
    const isLg = (this.state.windowWidth && this.state.windowWidth >= 1024);
    const tabsOrder = ['opportunity', 'growth', 'succession', 'learn'];
    const state = (typeof document !== 'undefined' && document.store?.getState?.()) || {};
    const userFromProps = this.props.current_user || {};
    const userFromStore = state.user || {};
    const mergedUser = { ...userFromProps, ...userFromStore };
    const companyFromProps = this.props.company || {};
    const companyFromStore = state.company || {};
    const mergedCompany = { ...companyFromProps, ...companyFromStore, ...(mergedUser?.company || {}) };
    const dealRoomWordComputed = getDealRoomBrandedName(mergedCompany) || getDealRoomSingleWord(mergedCompany);
    const hasCompletedAssessment = (() => {
      const explicit = mergedUser['has_completed_triggering_events?'];
      if (explicit !== undefined && explicit !== null) return Boolean(explicit);
      if (mergedUser.has_completed_triggering_events !== undefined) return Boolean(mergedUser.has_completed_triggering_events);
      const evts = Array.isArray(mergedUser.triggering_events) ? mergedUser.triggering_events : [];
      return evts.some((e) => e && e.completed_at);
    })();
    // Should we show the Growth/Exit decision card?
    const shouldShowDecision = Boolean(
      (hasCompletedAssessment) &&
      !mergedUser.permanently_snoozed_growth_exit_prompt &&
      !(mergedUser.growth_exit_snoozed_until && new Date(mergedUser.growth_exit_snoozed_until) > new Date()) &&
      !mergedUser.exit_decision_at
    ) && !this.state._hideDecisionCard;
    const computeVmiData = (u) => ([
      { subject: 'Identify', A: Math.max(1, Math.round(((u?.diag_readiness_total || 6) / 5))), fullMark: 6 },
      { subject: 'Protect', A: 3, fullMark: 6 },
      { subject: 'Build', A: Math.max(1, Math.round(((u?.diag_attractiveness_total || 6) / 5))), fullMark: 6 },
      { subject: 'Harvest', A: 3, fullMark: 6 },
      { subject: 'Manage', A: 3, fullMark: 6 },
    ]);
    const computeVmiPercent = (u) => {
      const data = computeVmiData(u);
      const total = data.reduce((sum, d) => sum + Number(d.A || 0), 0);
      const max = data.reduce((sum, d) => sum + Number(d.fullMark || 6), 0);
      const pct = max > 0 ? Math.round((total / max) * 100) : 0;
      return pct;
    };

    const getItemsForTab = (tabId) => {
      if (tabId === 'opportunity') {
        const items = [
          (
            <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
              <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
                <PartnerMiniGrid />
              </div>
            </div>
          ),
          (
            <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
              <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
                <RolodexMiniCard />
              </div>
            </div>
          ),
          (<SellerHeatMap />),
          (
            <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
              <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
                <PartialSalesMiniList />
              </div>
            </div>
          ),
        ];
        // Feature gate: ai_assistant at first position when enabled
        try {
          const state = (typeof document !== 'undefined' && document.store?.getState?.()) || {};
          const userFromProps = this.props.current_user || {};
          const userFromStore = state.user || {};
          const companyFromProps = this.props.company || {};
          const companyFromStore = state.company || {};
          // Prefer store values, merge over props
          const user = { ...userFromProps, ...userFromStore };
          const company = { ...companyFromProps, ...companyFromStore };
          const userFeatureList = ([])
            .concat(Array.isArray(user.features) ? user.features : [])
            .concat(Array.isArray(user.feature_types) ? user.feature_types : []);
          const companyFeatureList = ([])
            .concat(Array.isArray(company.features) ? company.features : [])
            .concat(Array.isArray(company.feature_types) ? company.feature_types : []);
          const userHas = userFeatureList.some((f) => String(f) === 'ai_assistant');
          const companyHas = companyFeatureList.some((f) => String(f) === 'ai_assistant');
          try { if (typeof window !== 'undefined') {
            const gate = { userHas, companyHas, userFeatures: userFeatureList, companyFeatures: companyFeatureList };
            window.__aiAssistantGate = gate;
            // Use console.log so it shows at Default level
            // eslint-disable-next-line no-console
            console.log('[HomeContainer] ai_assistant gate', gate);
          }} catch(_) {}
          if (userHas || companyHas) {
            const name = (this.props.current_user && this.props.current_user.name) || (document.store?.getState?.().user?.name) || 'there';
            items.unshift(<AIAssistantWidget currentUser={this.props.current_user} integrated={true} />);
          }
        } catch (_) {}
        return items;
      }
      if (tabId === 'growth') {
        return [
          (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><WealthGapMiniCard /></div>),
          (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><ValueGapDcfSnapshot /></div>),
          (
            <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border" data-tutorial-id="sprint-card">
              <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
                <MiniSprintDashboard />
              </div>
            </div>
          ),
        ];
      }
      if (tabId === 'succession') {
        return [
          (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><ContinuityPlanCard /></div>),
          (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><CatastrophicPlanCard /></div>),
          // Replace partial sale metrics with education resources
          (<div className="h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform"><SuccessionEducationCard /></div>),
        ];
      }
      if (tabId === 'learn') {
        return [
          (
            <div className="bg-succession-bg-primary rounded-lg border border-succession-border p-6 text-succession-text-secondary">
              Community coming soon.
            </div>
          ),
        ];
      }
      return [];
    };

    const setPage = (p) => this.setState((s) => ({ pageByTab: { ...s.pageByTab, [activeTab]: Math.max(0, p) } }));

    const findNextTabWithItems = (currentIdx) => {
      for (let i = 1; i <= tabsOrder.length; i += 1) {
        const idx = (currentIdx + i) % tabsOrder.length;
        const id = tabsOrder[idx];
        if (getItemsForTab(id).length > 0) return id;
      }
      return tabsOrder[currentIdx];
    };

    const findPrevTabWithItems = (currentIdx) => {
      for (let i = 1; i <= tabsOrder.length; i += 1) {
        const idx = (currentIdx - i + tabsOrder.length) % tabsOrder.length;
        const id = tabsOrder[idx];
        if (getItemsForTab(id).length > 0) return id;
      }
      return tabsOrder[currentIdx];
    };

    // Build flattened list across all groups to allow cross-group single-step navigation
    const itemsByGroup = tabsOrder.map((id) => ({ id, items: getItemsForTab(id) }));
    const groupStartIndex = {};
    let cursor = 0;
    itemsByGroup.forEach(({ id, items }) => {
      groupStartIndex[id] = cursor;
      cursor += (items?.length || 0);
    });
    const flatItems = itemsByGroup.reduce((acc, g) => acc.concat(g.items), []);
    const maxStart = Math.max(0, flatItems.length - effectivePageSize);
    const isDarkTheme = (() => {
      if (typeof this.state?._isDarkTheme === 'boolean') return this.state._isDarkTheme;
      try {
        const root = document.documentElement;
        if (root.classList.contains('succession-dark') || (document.body && document.body.classList && document.body.classList.contains('succession-dark'))) return true;
        const raw = getComputedStyle(root).getPropertyValue('--succession-bg-primary') || '';
        const parseRgb = (s) => {
          const m = String(s).match(/(\d+)[^\d]+(\d+)[^\d]+(\d+)/);
          return m ? { r: +m[1], g: +m[2], b: +m[3] } : null;
        };
        const parseHex = (s) => {
          const h = String(s).trim().replace('#','');
          if (h.length === 3) { const r=h[0]+h[0], g=h[1]+h[1], b=h[2]+h[2]; return { r: parseInt(r,16), g: parseInt(g,16), b: parseInt(b,16) }; }
          if (h.length === 6) { return { r: parseInt(h.slice(0,2),16), g: parseInt(h.slice(2,4),16), b: parseInt(h.slice(4,6),16) }; }
          return null;
        };
        const rgb = raw.includes('rgb') ? parseRgb(raw) : parseHex(raw);
        if (rgb) {
          const luminance = (0.2126*rgb.r + 0.7152*rgb.g + 0.0722*rgb.b) / 255;
          return luminance < 0.5; // treat as dark when background is dark
        }
      } catch (_) {}
      return false;
    })();
    const insightsCardStyle = isDarkTheme ? undefined : { backgroundColor: '#ffffff' };
    const showActivity = this.state._rightPanel !== 'getting_started';

    const findGroupForIndex = (idx) => {
      let current = tabsOrder[0];
      for (let i = 0; i < tabsOrder.length; i += 1) {
        const id = tabsOrder[i];
        const startIdx = groupStartIndex[id] ?? 0;
        const nextId = tabsOrder[i + 1];
        const nextStart = nextId ? (groupStartIndex[nextId] ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
        if (idx >= startIdx && idx < nextStart) { current = id; break; }
      }
      return current;
    };

    const handleNext = () => {
      this.setState((s) => {
        const nextFirst = Math.min((s.firstIndex || 0) + 1, maxStart);
        const nextTab = findGroupForIndex(nextFirst);
        return { firstIndex: nextFirst, activeTab: nextTab };
      });
    };

    const handlePrev = () => {
      this.setState((s) => {
        const prevFirst = Math.max((s.firstIndex || 0) - 1, 0);
        const prevTab = findGroupForIndex(prevFirst);
        return { firstIndex: prevFirst, activeTab: prevTab };
      });
    };
    window.locale = (this.props.railsContext && this.props.railsContext.i18nLocale) || window.locale || 'en';
    window.currency = (this.props.railsContext && this.props.railsContext.currency) || window.currency || 'USD';
    const locale = window.locale;
    return (
      <CurrencyProvider currency={(this.props.railsContext && this.props.railsContext.currency) || window.currency || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
      <SuccessionThemeProvider company={this.props.company} user={this.props.current_user}>
        <div className="p-4 text-succession-text-primary space-y-6 overflow-x-hidden max-w-[100vw]">
        {false && <TopCategories />}
        {/* Visualization selector aligned with "Suggestions For You" */}
        <div className="flex items-center justify-between mx-2 mb-2" data-tutorial-id="insights-dropdown">
          <h3 className="m-0 p-0">
            <span className="relative inline-flex items-center rounded-full border-2 border-succession-border bg-succession-bg-secondary text-succession-text-primary shadow-succession-md hover:border-succession-border-hover focus-within:ring-2 focus-within:ring-succession-focus-ring overflow-hidden transition-theme">
              <select
                className="appearance-none bg-transparent border-0 outline-none px-3 py-1.5 pr-8 cursor-pointer text-succession-text-primary font-inherit"
                value={this.state.insightsView}
                onChange={(e) => { this.setState({ insightsView: e.target.value }); try { if (window.ahoy?.track) window.ahoy.track('insights_dropdown_changed', { value: e.target.value }); } catch(_) {} }}
              >
                <option value="valuation_insight">Indication of Value</option>
                <option value="deal_room_insights">{this.state.dealRoomWord || dealRoomWordComputed} Funnel</option>
                <option value="succession_readiness">Succession Readiness</option>
                <option value="valuation_trend">Valuation Trend</option>
                <option value="peer_benchmark">Sprint Progress</option>
              </select>
              <span className="pointer-events-none absolute right-2 inset-y-0 flex items-center text-succession-text-secondary">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </span>
            </span>
          </h3>
          {/* Right panel nav aligned with dropdown */}
          {!shouldShowDecision && (
            <div className="flex items-center gap-2">
              <button
                className="px-2 py-1 rounded border border-succession-border text-succession-text-secondary hover:bg-white"
                onClick={() => this.setState({ _rightPanel: 'activity' })}
                aria-label="Previous"
                disabled={this.state._rightPanel === 'activity'}
              >
                ‹
              </button>
              <button
                className="px-2 py-1 rounded border border-succession-border text-succession-text-secondary hover:bg-white"
                onClick={() => this.setState({ _rightPanel: 'getting_started' })}
                aria-label="Next"
                disabled={this.state._rightPanel === 'getting_started'}
              >
                ›
              </button>
            </div>
          )}
        </div>

        {/* Insights row below carousel */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-10 md:mb-12 z-0 items-stretch overflow-hidden" style={{ height: isLg ? '451px' : 'auto' }}>
          {/* Left: 3/4 width card with dropdown */}
          <div className="lg:col-span-3" data-tutorial-id="insights-column">
            <div
              className={`overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 border border-succession-border ${
                isDarkTheme
                  ? 'bg-succession-bg-primary'
                  : ((this.state.insightsView === 'succession_readiness' || this.state.insightsView === 'valuation_trend' || this.state.insightsView === 'peer_benchmark' || this.state.insightsView === 'deal_journey' || this.state.insightsView === 'valuation_insight' || this.state.insightsView === 'deal_room_insights')
                      ? 'bg-transparent'
                      : 'bg-succession-bg-primary')
              }`}
              data-tutorial-id="succession-readiness-card"
              style={insightsCardStyle}
            >
              <div className="pl-2 pr-3 pt-2 pb-3 sm:pl-2 sm:pr-4 sm:py-3 flex flex-col gap-2 h-full">
                {this.state.insightsView === 'succession_readiness' ? (
                  hasCompletedAssessment ? (
                    <HomeVmiPanel />
                  ) : (
                    <div className="flex-1 min-h-[200px] flex flex-col items-center justify-center text-center">
                      <h3 className="text-succession-text-primary m-0 p-0">Complete Assessment</h3>
                      <h4 className="text-sm text-succession-text-secondary max-w-xl mx-auto mt-1">
                        Complete your owner assessment to unlock readiness insights. Track progress, reveal strengths and gaps, and prepare your next steps.
                      </h4>
                      <div className="mt-3">
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1.5 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover text-sm"
                          onClick={() => { try { if (window.ahoy?.track) window.ahoy.track('succession_readiness_start_assessment_clicked'); } catch(_) {} window.location.href = '/succession/plans'; }}
                        >
                          Start Assessment
                        </button>
                      </div>
                    </div>
                  )
                ) : this.state.insightsView === 'valuation_trend' ? (
                  <div className="h-full min-h-[200px]">
                    <ValuationTrend user={mergedUser} company={mergedUser?.company} />
                  </div>
                ) : this.state.insightsView === 'valuation_trend' ? (
                  <div className="h-full min-h-[200px]">
                    <ValuationTrend user={mergedUser} company={mergedUser?.company} />
                  </div>
                ) : this.state.insightsView === 'peer_benchmark' ? (
                  <div className="h-full min-h-[200px]">
                    <InsightsSprintProgress />
                  </div>
                ) : this.state.insightsView === 'deal_room_insights' ? (
                  <div className="h-full min-h-[200px]">
                    <DealRoomInsights />
                  </div>
                ) : this.state.insightsView === 'valuation_insight' ? (
                  <div className="h-full min-h-[200px]">
                    <ValuationInsight />
                  </div>
                ) : (
                  <div className="flex-1 min-h-[140px] flex items-center justify-center text-succession-text-secondary">
                    <div className="text-sm">Coming soon</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: 1/4 width contextual sidebar (stacks below insights on smaller viewports) */}
          <div className="lg:col-span-1 col-span-1 h-full min-h-0">
            {(() => {
              const shouldShowDecision = Boolean(
                (hasCompletedAssessment) &&
                !mergedUser.permanently_snoozed_growth_exit_prompt &&
                !(mergedUser.growth_exit_snoozed_until && new Date(mergedUser.growth_exit_snoozed_until) > new Date()) &&
                !mergedUser.exit_decision_at
              ) && !this.state._hideDecisionCard;
              if (shouldShowDecision) return <GrowthExitDecisionCard user={mergedUser} onCompleted={() => { try { if (window?.ahoy?.track) window.ahoy.track('GrowthExitPrompt', { action: 'hide_after_selection' }); } catch(_) {} try { this.setState({ _hideDecisionCard: true }); } catch(_) {} }} />;
              const toggle = () => this.setState((s) => ({ _rightPanel: s._rightPanel === 'getting_started' ? 'activity' : 'getting_started' }));
              return (
                <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg transition-all duration-300 border border-succession-border h-full min-h-0" data-tutorial-id="activity-feed">
                  <div className="px-4 py-4 sm:px-6 sm:py-5 h-full min-h-0 box-border grid" style={{ gridTemplateRows: 'auto 1fr' }}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-succession-text-primary m-0 p-0">{showActivity ? 'Your Activity' : 'Getting Started'}</h3>
                      <div />
                    </div>
                    <div className="mt-3 min-h-0 overflow-y-auto pr-1">
                      {showActivity ? (
                        <YourActivity user={mergedUser} maxItems={5} />
                      ) : (
                        <GettingStartedCard current_user={mergedUser} company={mergedCompany} features={mergedCompany?.features || []} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="flex items-center gap-6 relative" data-tutorial-id="explore-section">
          <div data-tutorial-id="explore-tabs" className="contents">
          <h3 className="text-succession-text-primary inline-flex items-center ml-2 m-0 p-0">Explore</h3>
          <ExploreTabs activeTab={activeTab} onTabChange={this.handleTabChange} className="flex-1" />
          {/* Carousel controls */}
          <div className="ml-auto flex items-center gap-2">
            <button
              className="px-2 py-1 rounded border border-succession-border text-succession-text-secondary hover:bg-white"
              onClick={handlePrev}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="px-2 py-1 rounded border border-succession-border text-succession-text-secondary hover:bg-white"
              onClick={handleNext}
              aria-label="Next"
            >
              ›
            </button>
          </div>
          </div>
        </div>

        <div id="panels" className="min-h-[200px] overflow-x-hidden overflow-y-visible min-w-0 w-full max-w-full" data-tutorial-id="explore-cards" style={{ contain: 'layout paint' }}>
          {(() => {
            return (
              <div className="mt-6 overflow-hidden">
                <ExploreCarousel items={flatItems} startIndex={this.state.firstIndex || 0} pageSize={effectivePageSize} itemHeight={500} />
              </div>
            );
          })()}
          {activeTab === 'growth' && null}
          {activeTab === 'learn' && (
            <div className="text-succession-text-secondary text-sm">Community coming soon.</div>
          )}
        </div>

        {/* Getting Started carousel hidden in favor of header-triggered drawer */}
        {false && (
          <GettingStartedCarousel current_user={this.props.current_user} company={this.props.company} features={(this.props.company && this.props.company.features) || []} />
        )}

        {/* Mount drawer so partner clicks open the profile with snackbar context */}
        <SnackbarProvider maxSnack={3}>
          <SuccessionDrawerWithListener />
        </SnackbarProvider>
        </div>
      </SuccessionThemeProvider>
      </IntlProvider>
      </CurrencyProvider>
    );
  }
}

export default HomeContainer;


